import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — anima un número de 0 hasta `value` con requestAnimationFrame.
 * Respeta `prefers-reduced-motion` (muestra el valor final directamente).
 *
 * @param {number} value      - valor final a alcanzar
 * @param {number} duration   - duración de la animación en ms (default 800)
 * @param {number} decimals   - nº de decimales a mostrar (default 0)
 * @param {string} prefix     - texto antes del número (ej. "€")
 * @param {string} suffix     - texto después del número (ej. " uds.")
 * @param {string} className  - clases del <span>
 */
export default function CountUp({
    value = 0,
    duration = 800,
    decimals = 0,
    prefix = "",
    suffix = "",
    className = "",
}) {
    const target = Number.isFinite(value) ? value : 0;
    const [display, setDisplay] = useState(0);
    const frameRef = useRef(null);
    // Valor actualmente mostrado: punto de partida de la próxima animación, para
    // interpolar desde el valor anterior al nuevo (no siempre desde 0).
    const displayRef = useRef(0);

    useEffect(() => {
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const from = displayRef.current;
        if (reduceMotion || duration <= 0 || from === target) {
            displayRef.current = target;
            setDisplay(target);
            return undefined;
        }

        const delta = target - from;
        let startTime = null;
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);

        const tick = (now) => {
            if (startTime === null) startTime = now;
            const progress = Math.min((now - startTime) / duration, 1);
            const current = from + delta * easeOut(progress);
            displayRef.current = current;
            setDisplay(current);
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(tick);
            }
        };

        frameRef.current = requestAnimationFrame(tick);
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [target, duration]);

    const formatted = display.toLocaleString("es-ES", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    return (
        <span className={className}>
            {prefix}
            {formatted}
            {suffix}
        </span>
    );
}
